import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useSearchParams, useNavigate } from 'react-router-dom';
import { getMachines } from '../services/api';
import { useGacha } from '../features/gacha';
import { useAuth } from '../features/auth';
import { useModal } from '../features/modal/ModalContext';
import MobileMachineModal from './MobileMachineModal';
import TokenIcon from './TokenIcon';
import Seo, { DEFAULT_DESCRIPTION, SITE_URL } from '../shared/Seo';
import { logPageView } from '../services/analytics';
import {
  createWelcomeMachine,
  WELCOME_MACHINE_ID,
  isWelcomeMachine,
} from '../constants/welcomeMachine';
import { DAILY_FREE_PLAY_LIMIT, getFreePlaysRemaining } from '../utils/freePlayUtils';

const MACHINE_STYLE = {
  brandColor: '#C12127',
  accent: '#21476B',
};

const BRAND_COLORS = {
  BANDAI: '#ee161f',
  'TAKARA TOMY': '#0e51a6',
  STASTO: '#f15928',
  BUSHIROAD: '#05923c',
};

const getBrandColor = (brand) => {
  if (!brand) {
    return MACHINE_STYLE.brandColor;
  }
  const normalizedBrand = brand.trim().toUpperCase();
  return BRAND_COLORS[normalizedBrand] ?? MACHINE_STYLE.brandColor;
};

const FALLBACK_IMAGE = '/assets/ponpon-fallback.svg';

// CHANGED: Carousel configuration - use 'featured' tag to control carousel display
const CAROUSEL_FEATURED_TAG = 'featured';
const HIDDEN_TAGS = ['featured']; // Tags that should not be displayed in the UI
const FALLBACK_PRIZE_POOL = [
  {
    id: 'fallback-prize-1',
    name: 'SD Gundam enamel pins',
    imageUrl: '',
    weight: 30,
    stock: 40,
    initialStock: 40,
  },
  {
    id: 'fallback-prize-2',
    name: 'Pokémon mascot straps',
    imageUrl: '',
    weight: 24,
    stock: 28,
    initialStock: 28,
  },
  {
    id: 'fallback-prize-3',
    name: 'Sailor Moon charms',
    imageUrl: '',
    weight: 18,
    stock: 20,
    initialStock: 20,
  },
  {
    id: 'fallback-prize-4',
    name: 'Mystery Capsule',
    imageUrl: '',
    weight: 12,
    stock: 12,
    initialStock: 12,
  },
];

const sanitizeString = (value, fallbackValue = '') =>
  typeof value === 'string' && value.trim().length > 0 ? value.trim() : fallbackValue;

const normalizePrizeRecord = (record = {}, index = 0) => {
  const fallback = FALLBACK_PRIZE_POOL[index % FALLBACK_PRIZE_POOL.length];
  const fallbackId = fallback ? fallback.id : `prize-${index}`;
  const fallbackName = fallback ? fallback.name : `Prize ${index + 1}`;
  const fallbackImageUrl = fallback ? fallback.imageUrl : FALLBACK_IMAGE;
  const fallbackWeight = fallback && Number.isFinite(fallback.weight) ? fallback.weight : 1;
  const fallbackStock =
    fallback && Number.isFinite(fallback.stock) ? Math.trunc(fallback.stock) : 0;
  const fallbackInitialStock =
    fallback && Number.isFinite(fallback.initialStock)
      ? Math.trunc(fallback.initialStock)
      : fallbackStock;

  const id = sanitizeString(record.id, fallbackId);
  const name = sanitizeString(record.name, fallbackName);
  const imageUrl = sanitizeString(record.imageUrl, fallbackImageUrl);
  const weightNumber = Number(record.weight);
  const weight =
    Number.isFinite(weightNumber) && weightNumber > 0 ? weightNumber : fallbackWeight;
  const stockNumber = Number(record.stock);
  const stock =
    Number.isFinite(stockNumber) && stockNumber >= 0
      ? Math.trunc(stockNumber)
      : fallbackStock;
  const initialStockNumber = Number(record.initialStock);
  const initialStock =
    Number.isFinite(initialStockNumber) && initialStockNumber >= 0
      ? Math.trunc(initialStockNumber)
      : fallbackInitialStock ?? stock;

  const nextPrize = {
    id,
    name,
    imageUrl,
    weight,
    stock,
    initialStock,
  };

  if (record.rarity) {
    nextPrize.rarity = sanitizeString(record.rarity);
  }

  if (record.metadata && typeof record.metadata === 'object') {
    nextPrize.metadata = { ...record.metadata };
  }

  return nextPrize;
};

const computeInventoryFromPrizePool = (prizePool = []) =>
  prizePool.reduce(
    (totals, prize) => {
      totals.inventoryTotal += Number.isFinite(prize.initialStock) ? prize.initialStock : 0;
      totals.inventoryAvailable += Number.isFinite(prize.stock) ? prize.stock : 0;
      return totals;
    },
    { inventoryTotal: 0, inventoryAvailable: 0 }
  );

const createFallbackMachine = () => ({
  id: 'ponpon-fallback',
  name: 'PonPon! Capsule',
  brand: 'PonPon!',
  tagline: 'Capsule joy delivered everywhere.',
  tokenCost: 3,
  tags: ['PonPon!', 'PonPon!'],
  imageUrl: FALLBACK_IMAGE,
  prizePool: FALLBACK_PRIZE_POOL.map((item) => ({ ...item })),
  updatedAt: new Date().toISOString(),
});

const decorateMachineRecord = (record = {}, index = 0) => {
  const fallback = createFallbackMachine();
  const id = sanitizeString(record.id, `machine-${index}`);

  const rawName = record.name || record.displayName || fallback.name;
  const displayName =
    typeof rawName === 'string'
      ? rawName
      : rawName && typeof rawName === 'object'
      ? sanitizeString(rawName.name || rawName.label, fallback.name)
      : fallback.name;

  const displayImage =
    (rawName && typeof rawName === 'object' && sanitizeString(rawName.imageUrl || rawName.picture)) ||
    sanitizeString(record.imageUrl, fallback.imageUrl);

  const tokenCostNumber = Number(record.tokenCost);
  const tokenCost = Number.isFinite(tokenCostNumber) && tokenCostNumber >= 0 ? tokenCostNumber : fallback.tokenCost;

  const tagline = sanitizeString(record.tagline, '');

  const tags = Array.isArray(record.tags)
    ? record.tags
        .filter((tag) => typeof tag === 'string' && tag.trim().length > 0)
        .map((tag) => tag.trim())
    : [];

  const prizePoolSource =
    Array.isArray(record.prizePool) && record.prizePool.length ? record.prizePool : fallback.prizePool;
  const prizePool = prizePoolSource.map((prize, prizeIndex) => normalizePrizeRecord(prize, prizeIndex));

  const { inventoryAvailable, inventoryTotal } = computeInventoryFromPrizePool(prizePool);
  const prizePoolWeightTotal = prizePool.reduce(
    (sum, prize) => sum + (Number(prize.weight) > 0 ? Number(prize.weight) : 0),
    0,
  );

  const metadata =
    record.metadata && typeof record.metadata === 'object'
      ? { ...record.metadata }
      : fallback.metadata || null;

  // A+: Optional SEO fields for future per-machine pages (data layer only)
  const seoTitle = sanitizeString(record.seoTitle || record.metaTitle, '') || null;
  const seoDescription = sanitizeString(record.seoDescription || record.metaDescription, '') || null;

  return {
    id,
    name: displayName,
    brand: sanitizeString(record.brand, fallback.brand),
    tagline: tagline || null,
    tokenCost,
    prizePool,
    prizePoolWeightTotal,
    tags,
    imageUrl: displayImage || FALLBACK_IMAGE,
    inventoryAvailable,
    inventoryTotal,
    metadata,
    updatedAt: record.updatedAt || fallback.updatedAt,
    seoTitle,
    seoDescription,
  };
};

const cloneMachineForState = (machine) => ({
  ...machine,
  prizePool: machine.prizePool.map((item) => ({ ...item })),
  tags: [...machine.tags],
  _isFeatured: machine._isFeatured ?? false, // CHANGED: Preserve featured flag
});

const MobileHomepage = () => {
  const { latestMachineState } = useGacha();
  const { loginPromptOpen, user, setLoginPromptOpen, setLoginInitialMode } = useAuth();
  const freePlaysRemaining = getFreePlaysRemaining(user);
  const { setMachineModalOpen } = useModal();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [machines, setMachines] = useState([]);
  const [selectedMachineId, setSelectedMachineId] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  const cardRefs = useRef({});
  const observerRef = useRef(null);
  const observerCleanupRef = useRef(null);
  const [mobileMachineModal, setMobileMachineModal] = useState(null);
  const previousLoginPromptOpenRef = useRef(loginPromptOpen);
  const hasScrolledOnMountRef = useRef(false);
  const heroStructuredData = useMemo(
    () => ({
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      name: 'PonPon! Mobile Featured Machines',
      description: 'PonPon! lets you spin real gachapon from your phone, store prizes in the Locker, and ship them in one batch.',
      url: 'https://ponpon.app/',
    }),
    []
  );

  useEffect(() => {
    let isMounted = true;
    const hydrateMachines = async () => {
      setIsLoading(true);
      try {
        const records = await getMachines();
        if (!isMounted || !Array.isArray(records) || records.length === 0) {
          return;
        }
        // CHANGED: Mark featured machines and filter hidden tags
        const decorated = records.map((record, index) => {
          const machine = decorateMachineRecord(record, index);
          const rawTags = Array.isArray(record.tags)
            ? record.tags.map((tag) => String(tag).trim().toLowerCase())
            : [];
          const isFeatured = rawTags.includes(CAROUSEL_FEATURED_TAG.toLowerCase());
          // Filter out hidden tags from display
          const displayTags = machine.tags.filter(
            (tag) => !HIDDEN_TAGS.includes(tag.toLowerCase())
          );
          return {
            ...machine,
            tags: displayTags,
            _isFeatured: isFeatured,
          };
        });
        if (!decorated.length) {
          return;
        }
        setMachines(decorated.map((machine) => cloneMachineForState(machine)));
        // CHANGED: Select first featured machine if available, otherwise first machine
        const featuredMachines = decorated.filter((m) => m._isFeatured);
        const initialMachine = featuredMachines.length > 0 ? featuredMachines[0] : decorated[0];
        setSelectedMachineId((prev) =>
          decorated.some((machine) => machine.id === prev) ? prev : initialMachine.id
        );
      } catch (error) {
        if (process.env.NODE_ENV !== 'test') {
          console.error('Failed to load machines (mobile homepage):', error);
        }
        const fallback = decorateMachineRecord(createFallbackMachine(), 0);
        setMachines([cloneMachineForState(fallback)]);
        setSelectedMachineId(fallback.id);
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    hydrateMachines();

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    if (!latestMachineState) return;

    // CHANGED: Mark featured status and filter hidden tags when updating machine state
    const decorated = decorateMachineRecord(latestMachineState);
    const rawTags = Array.isArray(latestMachineState.tags)
      ? latestMachineState.tags.map((tag) => String(tag).trim().toLowerCase())
      : [];
    const isFeatured = rawTags.includes(CAROUSEL_FEATURED_TAG.toLowerCase());
    const displayTags = decorated.tags.filter(
      (tag) => !HIDDEN_TAGS.includes(tag.toLowerCase())
    );
    const updatedMachine = {
      ...decorated,
      tags: displayTags,
      _isFeatured: isFeatured,
    };

    setMachines((prev) => {
      const existingIndex = prev.findIndex((machine) => machine.id === updatedMachine.id);
      if (existingIndex === -1) {
        return [...prev, cloneMachineForState(updatedMachine)];
      }
      const next = [...prev];
      next[existingIndex] = cloneMachineForState(updatedMachine);
      return next;
    });
  }, [latestMachineState]);

  // CHANGED: Prepend welcome machine until user closes the modal (so machine does not disappear while still viewing it)
  const displayMachines = useMemo(() => {
    const showWelcome =
      (user && user.welcomeSpinUsed !== true) ||
      mobileMachineModal?.id === WELCOME_MACHINE_ID;
    if (showWelcome && machines.length > 0) {
      const welcome = createWelcomeMachine();
      return [cloneMachineForState(welcome), ...machines];
    }
    return machines;
  }, [machines, user, mobileMachineModal]);

  useEffect(() => {
    if (
      displayMachines.length > 0 &&
      displayMachines[0].id === WELCOME_MACHINE_ID &&
      (!selectedMachineId || !displayMachines.some((m) => m.id === selectedMachineId))
    ) {
      setSelectedMachineId(WELCOME_MACHINE_ID);
    }
  }, [displayMachines, selectedMachineId]);

  const selectedMachine = useMemo(
    () =>
      displayMachines.find((machine) => machine.id === selectedMachineId) ||
      displayMachines[0] ||
      null,
    [displayMachines, selectedMachineId],
  );

  // CHANGED: Log machine page view to Google Analytics (Firebase) when URL has ?machine=id
  const lastTrackedMachineIdRef = useRef(null);
  useEffect(() => {
    const machineId = searchParams.get('machine');
    if (!machineId || !displayMachines.length) return;
    const machine = displayMachines.find((m) => m.id === machineId);
    if (!machine || lastTrackedMachineIdRef.current === machineId) return;
    lastTrackedMachineIdRef.current = machineId;
    const pageName = machine.seoTitle || machine.name || `Machine ${machineId}`;
    const pagePath = `/?machine=${machineId}`;
    logPageView(pageName, pagePath);
  }, [searchParams, displayMachines]);

  const displayPrizes = useMemo(() => {
    if (!selectedMachine?.prizePool?.length) {
      return [];
    }
    return selectedMachine.prizePool.slice(0, 10);
  }, [selectedMachine]);
  const selectedPrizeWeightTotal = useMemo(() => {
    if (!selectedMachine?.prizePool?.length) {
      return 0;
    }
    if (Number(selectedMachine.prizePoolWeightTotal) > 0) {
      return Number(selectedMachine.prizePoolWeightTotal);
    }
    return selectedMachine.prizePool.reduce(
      (sum, prize) => sum + (Number(prize.weight) > 0 ? Number(prize.weight) : 0),
      0,
    );
  }, [selectedMachine]);

  // CHANGED: unauthenticated CTA opens enroll modal (first spin discount removed)
  const ctaButtonText = useMemo(() => {
    if (!user) return 'Sign up to collect up to 5 tokens';
    if (isWelcomeMachine(selectedMachine)) return 'Spin Now & Win up to 5 Tokens!';
    return 'Spin Now!';
  }, [user, selectedMachine]);

  useEffect(() => {
    if (!displayMachines.length) {
      return undefined;
    }
    const entries = Object.entries(cardRefs.current);
    if (!entries.length) {
      return undefined;
    }
    const observer = new IntersectionObserver(
      (ioEntries) => {
        let bestEntry = null;
        ioEntries.forEach((entry) => {
          if (!bestEntry || entry.intersectionRatio > bestEntry.intersectionRatio) {
            bestEntry = entry;
          }
        });
        if (bestEntry?.isIntersecting) {
          const machineId = bestEntry.target.getAttribute('data-machine-id');
          if (machineId && machineId !== selectedMachineId) {
            setSelectedMachineId(machineId);
          }
        }
      },
      {
        root: null,
        threshold: [0.5, 0.75, 1],
      }
    );
    entries.forEach(([, node]) => {
      if (node) {
        observer.observe(node);
      }
    });
    observerRef.current = observer;
    observerCleanupRef.current = () => {
      observer.disconnect();
    };
    return () => {
      observer.disconnect();
    };
  }, [displayMachines, selectedMachineId]);

  useEffect(
    () => () => {
      observerCleanupRef.current?.();
    },
    [],
  );

  // CHANGED: auto-scroll to top on navigation and page refresh (same pattern as desktop homepage and support pages)
  useEffect(() => {
    if (location.pathname !== '/') {
      return;
    }

    const smoothScroll = () => window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
    const ensureTop = () => {
      const doc = document?.documentElement;
      const body = document?.body;
      const current = (doc?.scrollTop || 0) + (body?.scrollTop || 0);
      if (current > 0) {
        window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
      }
    };

    // On first mount (page refresh), use instant scroll for better UX
    if (!hasScrolledOnMountRef.current) {
      window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
      hasScrolledOnMountRef.current = true;
      // Then ensure it stays at top with smooth scroll
      const retryId = window.setTimeout(ensureTop, 100);
      const fallbackId = window.setTimeout(() => {
        window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
      }, 500);
      return () => {
        window.clearTimeout(retryId);
        window.clearTimeout(fallbackId);
      };
    }

    // On navigation (route change), use smooth scroll
    smoothScroll();
    const retryId = window.setTimeout(ensureTop, 220);
    const fallbackId = window.setTimeout(() => {
      window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
    }, 900);

    return () => {
      window.clearTimeout(retryId);
      window.clearTimeout(fallbackId);
    };
  }, [location.pathname]);

  // CHANGED: auto-scroll to top when login modal closes (user logs in successfully)
  useEffect(() => {
    if (location.pathname !== '/') {
      return;
    }

    const wasOpen = previousLoginPromptOpenRef.current;
    const isNowClosed = wasOpen && !loginPromptOpen;

    if (isNowClosed) {
      const smoothScroll = () => window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
      const ensureTop = () => {
        const doc = document?.documentElement;
        const body = document?.body;
        const current = (doc?.scrollTop || 0) + (body?.scrollTop || 0);
        if (current > 0) {
          window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
        }
      };

      // Small delay to ensure modal is fully closed
      const scrollId = window.setTimeout(smoothScroll, 100);
      const retryId = window.setTimeout(ensureTop, 320);
      const fallbackId = window.setTimeout(() => {
        window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
      }, 1000);

      return () => {
        window.clearTimeout(scrollId);
        window.clearTimeout(retryId);
        window.clearTimeout(fallbackId);
      };
    }

    previousLoginPromptOpenRef.current = loginPromptOpen;
  }, [loginPromptOpen, location.pathname]);

  const handleSelectMachine = useCallback(
    (machineId) => {
      setSelectedMachineId(machineId);
      const card = cardRefs.current[machineId];
      if (card) {
        card.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
      }
    },
    [],
  );

  const handleMachineCardClick = useCallback(
    (machine) => {
      handleSelectMachine(machine.id);
      setMobileMachineModal(machine);
      navigate({ pathname: '/', search: `?machine=${machine.id}` }, { replace: false });
    },
    [handleSelectMachine, navigate],
  );

  // Sync URL ?machine=id with modal: open modal when URL has machine; close when URL cleared (e.g. back)
  useEffect(() => {
    const machineId = searchParams.get('machine');
    if (machineId && displayMachines.length > 0) {
      const machine = displayMachines.find((m) => m.id === machineId);
      if (machine) {
        handleSelectMachine(machineId);
        setMobileMachineModal(machine);
      }
    } else if (!machineId && mobileMachineModal) {
      setMobileMachineModal(null);
    }
  }, [searchParams, displayMachines, handleSelectMachine, mobileMachineModal]);

  // Update modal context when machine modal opens/closes
  useEffect(() => {
    setMachineModalOpen(Boolean(mobileMachineModal));
  }, [mobileMachineModal, setMachineModalOpen]);

  const availableCount = useMemo(
    () => displayMachines.filter((machine) => machine.inventoryAvailable > 0).length,
    [displayMachines],
  );

  // CHANGED: Sort machines - welcome first, then featured
  const sortedMachines = useMemo(() => {
    return [...displayMachines].sort((a, b) => {
      if (a._isWelcome && !b._isWelcome) return -1;
      if (!a._isWelcome && b._isWelcome) return 1;
      if (a._isFeatured && !b._isFeatured) return -1;
      if (!a._isFeatured && b._isFeatured) return 1;
      return 0;
    });
  }, [displayMachines]);

  const renderCarousel = () => {
    // CHANGED: Show skeleton while loading (don't wait for machines.length)
    if (isLoading) {
      return (
        <div className="flex gap-3 overflow-hidden px-4">
          {[0, 1, 2].map((index) => (
            <div
              // eslint-disable-next-line react/no-array-index-key
              key={index}
              className="min-w-[78%] rounded-3xl border border-white/60 bg-white/80 p-4 shadow-md"
            >
              <div className="h-32 w-full animate-pulse rounded-2xl bg-ponpon-sky/40" />
              <div className="mt-4 h-4 w-2/3 animate-pulse rounded-full bg-ponpon-sky/40" />
              <div className="mt-2 h-3 w-1/2 animate-pulse rounded-full bg-ponpon-sky/30" />
            </div>
          ))}
        </div>
      );
    }
    if (!displayMachines.length) {
      return null;
    }

    return (
      <div
        className="mobile-homepage-carousel flex gap-3 overflow-x-auto pb-2"
        style={{ scrollSnapType: 'x mandatory' }}
        aria-label="Featured machines"
      >
        {sortedMachines.map((machine) => {
          const soldOut = machine.inventoryAvailable === 0;
          const lowStock =
            !soldOut &&
            machine.inventoryAvailable <= Math.max(5, Math.round(machine.inventoryTotal * 0.1));
          const isActive = machine.id === selectedMachine?.id;
          return (
            <button
              key={machine.id}
              type="button"
              onClick={() => handleMachineCardClick(machine)}
              ref={(el) => {
                if (el) {
                  cardRefs.current[machine.id] = el;
                }
              }}
              data-machine-id={machine.id}
              className={[
                'mobile-homepage-machine-card min-w-[78%] snap-center rounded-3xl border p-4 text-left shadow-md transition flex h-full flex-col gap-3 min-h-[360px]',
                isActive
                  ? 'mobile-homepage-machine-card-active border-white/70 bg-white/90'
                  : 'mobile-homepage-machine-card-inactive border-transparent bg-transparent shadow-none',
              ].join(' ')}
            >
              <div className="mobile-machine-card-body flex flex-1 flex-col gap-3">
                <div className="mobile-machine-card-preview flex flex-shrink-0 items-start justify-start">
                  <div className="machine-unit mobile-machine-card">
                    <div className="machine-top" style={{ borderColor: `${MACHINE_STYLE.accent}33` }}>
                      <div className="machine-brand" style={{ color: getBrandColor(machine.brand) }}>
                        {machine.brand || 'PonPon!'}
                      </div>
                    </div>
                    <div className="machine-glass">
                      <img
                        src={machine.imageUrl || FALLBACK_IMAGE}
                        alt={machine.name}
                        className="max-h-full max-w-full object-contain"
                        loading="lazy"
                        decoding="async"
                      />
                    </div>
                  </div>
                </div>
                <p className="mobile-machine-card-name text-lg font-semibold text-ponpon-navy line-clamp-2 h-[3.4rem] flex-shrink-0">
                  {machine.name || '\u00A0'}
                </p>
              </div>
              <div className="machine-meta mobile-machine-card-meta flex flex-wrap gap-1.5">
                <span className={`machine-pill text-xs ${machine.tokenCost === 0 ? `machine-pill-free ${freePlaysRemaining === 0 ? 'is-exhausted' : ''}` : 'machine-pill-token'}`}>
                  {machine.tokenCost === 0 ? (
                    freePlaysRemaining === 0
                      ? 'TOMORROW'
                      : `Free Spin ${freePlaysRemaining}/${DAILY_FREE_PLAY_LIMIT}`
                  ) : (
                    <>
                      <TokenIcon className="w-3.5 h-3.5" alt="tokens" />
                      {machine.tokenCost} token{machine.tokenCost === 1 ? '' : 's'}
                    </>
                  )}
                </span>
                <span
                  className={[
                    'machine-pill machine-pill-stock text-xs',
                    soldOut ? 'pill-muted' : lowStock ? 'pill-warn' : 'pill-stock',
                  ].join(' ')}
                >
                  {soldOut ? 'Sold out' : `${machine.inventoryAvailable} left`}
                </span>
              </div>
            </button>
          );
        })}
      </div>
    );
  };

  const machineIdFromUrl = searchParams.get('machine');
  const machineForSeo =
    machineIdFromUrl && displayMachines.length > 0
      ? displayMachines.find((m) => m.id === machineIdFromUrl)
      : null;

  return (
    <div className="mobile-homepage flex flex-col gap-3 px-0 pt-0 pb-3 mt-0">
      <Seo
        title={machineForSeo ? `PonPon! | ${machineForSeo.seoTitle || machineForSeo.name}` : undefined}
        description={
          machineForSeo
            ? machineForSeo.seoDescription || machineForSeo.tagline || DEFAULT_DESCRIPTION
            : DEFAULT_DESCRIPTION
        }
        canonical={machineForSeo ? `${SITE_URL}/?machine=${machineForSeo.id}` : `${SITE_URL}/`}
        image="/assets/Background.png"
        jsonLd={heroStructuredData}
      />
      <h1 className="sr-only">Online Gachapon – Spin Authentic Japanese Capsule Toys in the USA</h1>
      <section className="mobile-homepage-carousel-section -mx-4 space-y-2.5">
        <div className="mobile-homepage-carousel-header flex items-center justify-between px-4">
          <h2 className="mobile-homepage-heading text-xl font-semibold text-ponpon-navy">Gachapon Machines</h2>
          <span className="mobile-homepage-availability rounded-full border border-ponpon-navy/15 bg-white/80 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-ponpon-navy/60 shadow-sm">
            {availableCount} available
          </span>
        </div>
        <div className="mobile-homepage-carousel-wrapper">
          {renderCarousel()}
        </div>
      </section>

      <section className="mobile-homepage-detail rounded-3xl border border-white/70 bg-white/90 p-2.5 shadow-lg">
        {selectedMachine ? (
          <div className="mobile-machine-detail flex flex-col gap-2.5">
            <div className="mobile-machine-prize-header space-y-2.5">
              <div className="mobile-machine-prize-title flex items-center justify-between text-xs font-semibold text-ponpon-navy/60">
                <p className="uppercase tracking-wide">
                  What's inside
                </p>
              </div>
              {displayPrizes.length ? (
                <div
                  className="mobile-machine-prize-list flex gap-2.5 overflow-x-auto pb-1.5"
                  style={{ scrollSnapType: 'x mandatory', minHeight: '180px' }}
                >
                  {displayPrizes.map((prize) => {
                    const rawWeight = Number(prize.weight) > 0 ? Number(prize.weight) : 0;
                    const totalWeight = selectedPrizeWeightTotal > 0 ? selectedPrizeWeightTotal : 1;
                    const computedPercent =
                      rawWeight > 0 ? (rawWeight / totalWeight) * 100 : null;
                    const weightPercentDisplay =
                      computedPercent === null
                        ? null
                        : computedPercent <= 1
                        ? '<1%'
                        : `${Math.round(computedPercent)}%`;
                    return (
                    <div
                      key={prize.id || prize.name}
                      className="mobile-machine-prize-card min-w-[128px] snap-start rounded-2xl border border-white/60 bg-white/95 px-3 py-2.5 text-center shadow-sm flex h-[150px] flex-col"
                    >
                      <div
                        className="mobile-machine-prize-thumb prize-card-thumb mb-1.5 rounded-2xl bg-white"
                        style={{ flexBasis: '80%' }}
                      >
                        {prize.imageUrl ? (
                          <img
                            src={prize.imageUrl}
                            alt={prize.name}
                            className="h-full w-full object-contain"
                            loading="lazy"
                            decoding="async"
                          />
                        ) : (
                          <span className="text-2xl">🎁</span>
                        )}
                      </div>
                      <p className="text-[0.65rem] font-semibold text-ponpon-navy line-clamp-2 h-[2.5rem] flex-shrink-0">
                        {prize.name}
                      </p>
                      {!isWelcomeMachine(selectedMachine) && weightPercentDisplay ? (
                        <p className="text-[0.55rem] font-semibold uppercase tracking-wide text-ponpon-navy/60">
                          {weightPercentDisplay}
                        </p>
                      ) : null}
                    </div>
                    );
                  })}
                </div>
              ) : (
                <div
                  className="mobile-machine-prize-placeholder flex h-24 items-center justify-center rounded-2xl border border-dashed border-ponpon-navy/20 bg-white/60 text-sm text-ponpon-navy/60"
                >
                  Prize list coming soon.
                </div>
              )}
            </div>
          </div>
        ) : (
          <div className="mobile-machine-detail-empty space-y-2 text-center text-sm text-ponpon-navy/60">
            <p>Select a machine from the carousel to view tokens, availability, and series details.</p>
          </div>
        )}
      </section>

      <div className="mobile-machine-cta-bar sticky bottom-4 z-20 px-4">
        <button
          type="button"
          onClick={() => {
            if (!user) {
              setLoginInitialMode?.('signup');
              setLoginPromptOpen?.(true);
            } else if (selectedMachine) {
              // CHANGED: Sync URL with modal so the sync effect does not clear the modal
              setMobileMachineModal(selectedMachine);
              navigate({ pathname: '/', search: `?machine=${selectedMachine.id}` }, { replace: false });
            }
          }}
          disabled={user && !selectedMachine}
          className="mobile-machine-cta-button w-full rounded-full bg-ponpon-coral px-4 py-3 text-sm font-semibold text-white shadow-[0_15px_30px_rgba(226,58,50,0.35)] transition hover:bg-[#E23A32] disabled:cursor-not-allowed disabled:bg-ponpon-coral/60"
        >
          {ctaButtonText}
        </button>
      </div>
      <MobileMachineModal 
        machine={mobileMachineModal} 
        onClose={() => {
          setMobileMachineModal(null);
          navigate({ pathname: '/', search: '' }, { replace: true });
        }} 
      />
    </div>
  );
};

export default MobileHomepage;
